import AddIcon from '@mui/icons-material/Add'
import { Box, Chip, IconButton, Tooltip } from '@mui/material'
import { BinIcon, EditIcon, TomTable, useTomTable } from '@tom-ui/ui'
import { ContainerLengthBayMappingDto } from '@yard/app/api/dg-config-api'
import { Permission } from '@yard/app/models/permission.enum'
import { useYardStores } from '@yard/AppProvider'
import { useTranslate } from '@tolgee/react'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'

const ContainerLengthBayMappingsTable = observer(() => {
  const { t } = useTranslate()
  const { containerLengthBayMappingsTableUIStore, authStore } = useYardStores()
  const canWrite = authStore.hasPermission(Permission.WriteDangerousGoodsSettings)

  useEffect(() => {
    containerLengthBayMappingsTableUIStore.loadAll()
  }, [containerLengthBayMappingsTableUIStore])

  const columns = useMemo<MRT_ColumnDef<ContainerLengthBayMappingDto>[]>(
    () => [
      {
        accessorKey: 'length',
        header: t('containerLength', 'Container length'),
      },
      {
        accessorKey: 'bayCount',
        header: t('bayCount', 'Bay count'),
      },
      {
        accessorKey: 'isStandard',
        header: t('mappingType', 'Type'),
        Cell: ({ row }) => (
          <Chip
            label={row.original.isStandard ? t('standard', 'Standard') : t('custom', 'Custom')}
            color={row.original.isStandard ? 'secondary' : 'default'}
          />
        ),
      },
    ],
    [t],
  )

  const table = useTomTable<ContainerLengthBayMappingDto>({
    columns,
    data: containerLengthBayMappingsTableUIStore.mappings,
    enableSorting: false,
    autoResetPageIndex: false,
    enablePagination: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableTopToolbar: canWrite,
    enableRowActions: canWrite,
    enableExpanding: false,
    enableRowSelection: false,
    renderTopToolbarCustomActions: () =>
      canWrite ? (
        <Tooltip title={t('addContainerLengthMapping', 'Add container length mapping')}>
          <IconButton
            aria-label='add'
            onClick={containerLengthBayMappingsTableUIStore.openCreateDialog}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      ) : null,
    renderRowActions: ({ row }) =>
      canWrite ? (
        <Box>
          <Tooltip title={t('edit', 'Edit')}>
            <IconButton
              aria-label='edit'
              onClick={() => containerLengthBayMappingsTableUIStore.openEditDialog(row.original.id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          {!row.original.isStandard && (
            <Tooltip title={t('delete', 'Delete')}>
              <IconButton
                aria-label='delete'
                onClick={() => containerLengthBayMappingsTableUIStore.delete(row.original.id)}
              >
                <BinIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : null,
    positionActionsColumn: 'last',
  })

  return <TomTable table={table} />
})

export default ContainerLengthBayMappingsTable
